import React, { type ReactNode, useEffect, useState } from 'react';
import styled from 'styled-components';
import { colors } from '@/constants/colors';

/**
 * The device card, with a back.
 *
 * Ray approved this shape: a "System Info ›" button turns the whole card over,
 * and "‹" turns it back. It is one card, not a panel that appears next to one —
 * the operator is already looking at the vendo they are asking about.
 *
 * WHY THE TWO FACES SHARE A GRID CELL: both are placed in row 1, column 1, so
 * the container is naturally as tall as the TALLER of them. The mockup did this
 * by measuring both faces in JavaScript on load and on resize, and had to
 * un-position the back face to measure it — which is fragile and, in a list of
 * hundreds of cards, a lot of layout work on every resize. The grid does the
 * same job with no measurement at all.
 *
 * 🔴 THE HIDDEN FACE MUST NOT BE CLICKABLE OR FOCUSABLE. `backface-visibility:
 * hidden` only stops it being SEEN. Its buttons stay in the tab order and still
 * take clicks through the face in front, so an operator tabbing across a card
 * would land on controls they cannot see — and the front face has eight of them
 * (settings, kebab, activate, sub-vendos…), so this is not a corner case.
 *
 * `visibility: hidden` is what removes an element from both, and it is used
 * rather than React's `inert` because this is React 18, where `inert` is not a
 * typed DOM attribute and would need a cast to reach the element at all.
 * DELAYED BY HALF THE FLIP: applied immediately, the outgoing face would vanish
 * at the start of the turn instead of at the midpoint, and the card would look
 * empty while it rotated. Removing the delay on the way back in means the
 * incoming face is focusable from the first frame.
 */

export interface DeviceCardFlipProps {
   $online: boolean;
   /** The front. Given a function that turns the card over. */
   children: (flip: () => void) => ReactNode;
   /** The back. Given a function that turns it back, and whether it is showing
    *  — the rings only sweep when someone is actually looking at them. */
   back: (unflip: () => void, active: boolean) => ReactNode;
}

export default function DeviceCardFlip({
   $online,
   children,
   back,
}: DeviceCardFlipProps) {
   const [flipped, setFlipped] = useState(false);

   /**
    * Which face currently DEFINES the card's height.
    *
    * 🔴 Ray, on the shipped card: "when close the system info it left the empty
    * space". Both faces sat in the same grid cell, so the card was permanently
    * as tall as the TALLER of them — and the back has more rows than the front.
    * A short front card therefore carried a band of dead space under its
    * buttons, on every vendo, whether or not anyone had ever flipped it.
    *
    * So only the visible face takes part in layout; the other is taken out of
    * flow. Swapped at the MIDPOINT of the turn, when the card is edge-on and a
    * height change cannot be seen — doing it on click makes the card jump
    * before it has started rotating.
    */
   const [measuredBack, setMeasuredBack] = useState(false);

   useEffect(() => {
      // matchMedia is guarded because jsdom does not implement it, and an
      // unguarded call takes the mount test down for an unrelated reason.
      const reduce =
         typeof window !== 'undefined' &&
         typeof window.matchMedia === 'function' &&
         window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (reduce) {
         setMeasuredBack(flipped);
         return;
      }
      const id = window.setTimeout(() => setMeasuredBack(flipped), FLIP_MS / 2);
      return () => window.clearTimeout(id);
   }, [flipped]);

   return (
      <Stage>
         <Flipper $flipped={flipped}>
            <Front
               $online={$online}
               $hidden={flipped}
               $measured={!measuredBack}
               aria-hidden={flipped}
            >
               {children(() => setFlipped(true))}
            </Front>
            <Back
               $online={$online}
               $hidden={!flipped}
               $measured={measuredBack}
               aria-hidden={!flipped}
            >
               {back(() => setFlipped(false), flipped)}
            </Back>
         </Flipper>
      </Stage>
   );
}

/** How long the turn takes. Shared by the CSS and the layout swap above. */
const FLIP_MS = 550;

const Stage = styled.div`
   perspective: 1600px;
   min-width: 0;
`;

const Flipper = styled.div<{ $flipped: boolean }>`
   display: grid;
   position: relative;
   transform-style: preserve-3d;
   transition: transform ${FLIP_MS}ms cubic-bezier(0.22, 0.8, 0.28, 1);
   transform: rotateY(${({ $flipped }) => ($flipped ? '180deg' : '0deg')});

   /* An operator who has asked for less motion still gets the other face — it
      simply swaps instead of turning. */
   @media (prefers-reduced-motion: reduce) {
      transition: none;
   }
`;

const face = `
   /* The face that is showing sits in the grid cell and gives the card its
      height; the other is lifted out of flow (see the measured() helper). They can
      never both be in flow, or the card is permanently as tall as the taller
      one — which is exactly the dead space under the front that Ray found. */
   grid-area: 1 / 1 / 2 / 2;
   background: ${colors.WHITE};
   border-radius: 12px;
   padding: 10px 12px;
   display: flex;
   flex-direction: column;
   gap: 8px;
   min-width: 0;
   backface-visibility: hidden;
   -webkit-backface-visibility: hidden;
`;

/**
 * Out of flow unless this face is the one being measured. `inset: 0` keeps it
 * exactly over the card while it waits, so the turn still shows a full face
 * rather than a collapsed one.
 */
const measured = ($measured: boolean) =>
   $measured ? '' : 'position: absolute; inset: 0;';

/** Hidden at the midpoint of the turn, shown again instantly. See the note above. */
const hide = ($hidden: boolean) => `
   visibility: ${$hidden ? 'hidden' : 'visible'};
   pointer-events: ${$hidden ? 'none' : 'auto'};
   transition: visibility 0s linear ${$hidden ? '0.275s' : '0s'};
   @media (prefers-reduced-motion: reduce) {
      transition: none;
   }
`;

const Front = styled.div<{ $online: boolean; $hidden: boolean; $measured: boolean }>`
   ${face}
   border: 1px solid ${({ $online }) => ($online ? `${colors.SUCCESS}30` : colors.GRAY12)};
   ${({ $hidden }) => hide($hidden)}
   ${({ $measured }) => measured($measured)}
`;

const Back = styled.div<{ $online: boolean; $hidden: boolean; $measured: boolean }>`
   ${face}
   border: 1px solid ${({ $online }) => ($online ? `${colors.SUCCESS}30` : colors.GRAY12)};
   transform: rotateY(180deg);
   ${({ $hidden }) => hide($hidden)}
   ${({ $measured }) => measured($measured)}
`;
